/**
 * FirmRMDReport — Firm-wide RMD summary report.
 *
 * Shows all RMD profiles across every household with status,
 * distribution progress, and deadline tracking.
 */

import { useMemo, useState } from "react";
import { Alert, Badge, Card, Col, Form, Row, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useAuthenticatedFetch } from "../lib/api";
import { formatCurrency } from "../lib/numbers";
import type {
  SerializedObject,
  WrappedApiResponse,
} from "../../../api/src/lib";
import type { FirmRMDSummary } from "../../../api/src/rmd/rmd-lib";
import { getRuleLabelForId } from "../clients/household/rmd-helpers";
import TabContainerWithTabs from "../components/TabContainer";
import ReportsNav from "./ReportsNav";

const CY = new Date().getFullYear();

function fmt(n: number): string {
  return formatCurrency(n, 2);
}

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "satisfied", label: "Satisfied" },
  { value: "none_required", label: "None Req." },
  { value: "no_data", label: "No Data" },
] as const;

const statusBadgeProps = (status: string): { bg: string; label: string } => {
  switch (status) {
    case "satisfied":
      return { bg: "success", label: "Satisfied" };
    case "partial":
      return { bg: "warning", label: "Partial" };
    case "none_required":
      return { bg: "info", label: "None Req." };
    case "pending":
      return { bg: "danger", label: "Pending" };
    default:
      return { bg: "secondary", label: "No Data" };
  }
};

const FirmRMDReport = () => {
  const yearOptions = useMemo(() => {
    const years: number[] = [];
    for (let y = CY - 2; y <= CY + 2; y++) years.push(y);
    return years;
  }, []);

  const [year, setYear] = useState(CY);
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  // `GET /v1/rmd/firm-summary` (APP-1653).
  const url = `/v1/rmd/firm-summary?year=${year}`;
  const {
    data: response,
    isPending,
    isError,
  } = useAuthenticatedFetch<
    WrappedApiResponse<SerializedObject<FirmRMDSummary>>
  >(url);
  const apiData = response?.data;
  const summary = apiData?.summary;
  const accounts = apiData?.accounts ?? [];

  const filteredRows = useMemo(() => {
    if (!accounts.length) return [];
    if (statusFilter === "all") return accounts;
    if (statusFilter === "no_data")
      return accounts.filter((r) => !r.status || r.status === "no_data");
    return accounts.filter((r) => r.status === statusFilter);
  }, [accounts, statusFilter]);

  return (
    <TabContainerWithTabs tabs={ReportsNav}>
      <h5 className="mb-3">Firm RMD Report</h5>

      {/* Filters */}
      <Row className="mb-3">
        <Col md={2}>
          <Form.Group>
            <Form.Label>Year</Form.Label>
            <Form.Select
              value={year}
              onChange={(e) => setYear(+e.target.value)}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      {isPending && <Alert variant="info">Loading firm RMD data...</Alert>}
      {isError && (
        <Alert variant="danger">Failed to load firm RMD summary.</Alert>
      )}

      {summary && (
        <>
          {/* Summary cards */}
          <Row className="mb-3">
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Total Accounts</small>
                  <h4 className="mb-0">{summary.totalAccounts}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Total Required</small>
                  <h4 className="mb-0">{fmt(summary.totalRequired)}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">
                    Total Distributed
                  </small>
                  <h4 className="mb-0">{fmt(summary.totalDistributed)}</h4>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="text-center">
                <Card.Body className="py-2">
                  <small className="text-muted d-block">Total Remaining</small>
                  <h4
                    className={`mb-0 ${summary.totalRemaining > 0 ? "text-danger" : "text-success"}`}
                  >
                    {fmt(summary.totalRemaining)}
                  </h4>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data table */}
          <Card>
            <Table hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Household</th>
                  <th>Account</th>
                  <th>Owner</th>
                  <th>Type</th>
                  <th>Rule</th>
                  <th className="text-end">Required</th>
                  <th className="text-end">Distributed</th>
                  <th className="text-end">Remaining</th>
                  <th>Status</th>
                  <th>Deadline</th>
                  <th className="text-end">Days Left</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="text-center text-muted py-4">
                      No RMD accounts found for {year}
                      {statusFilter !== "all"
                        ? ` with status "${
                            STATUS_OPTIONS.find((o) => o.value === statusFilter)
                              ?.label ?? statusFilter
                          }"`
                        : ""}
                      .
                    </td>
                  </tr>
                )}
                {filteredRows.map((row, idx) => {
                  const badge = statusBadgeProps(row.status);
                  const rowHref = `/clients/households/${row.householdId}/tax?tab=rmd`;
                  return (
                    <tr
                      key={idx}
                      role="button"
                      tabIndex={0}
                      aria-label={`Open ${row.householdName} RMD tracking`}
                      onClick={(e) => {
                        // Don't hijack clicks on real anchors or interactive
                        // elements inside the row.
                        const target = e.target as HTMLElement;
                        if (target.closest("a,button,input,select,textarea")) {
                          return;
                        }
                        navigate(rowHref);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          navigate(rowHref);
                        }
                      }}
                      style={{ cursor: "pointer" }}
                    >
                      <td>
                        <Link to={rowHref} className="text-decoration-none">
                          {row.householdName}
                        </Link>
                      </td>
                      <td>
                        <strong>{row.accountLabel}</strong>
                      </td>
                      <td>{row.contactName}</td>
                      <td>
                        <Badge
                          bg={row.isInherited ? "warning" : "primary"}
                          className={row.isInherited ? "text-dark" : ""}
                        >
                          {row.isInherited ? "Inherited" : "Own"}
                        </Badge>
                      </td>
                      <td>
                        <small>{getRuleLabelForId(row.ruleId)}</small>
                      </td>
                      <td className="text-end">
                        {row.requiredAmount > 0
                          ? fmt(row.requiredAmount)
                          : "\u2014"}
                      </td>
                      <td className="text-end">
                        {row.distributedAmount > 0
                          ? fmt(row.distributedAmount)
                          : "\u2014"}
                      </td>
                      <td className="text-end">
                        {row.remainingAmount > 0
                          ? fmt(row.remainingAmount)
                          : "\u2014"}
                      </td>
                      <td>
                        <Badge bg={badge.bg}>{badge.label}</Badge>
                      </td>
                      <td>
                        {row.deadline
                          ? new Date(row.deadline).toLocaleDateString(
                              undefined,
                              { timeZone: "UTC" },
                            )
                          : "\u2014"}
                      </td>
                      <td className="text-end">
                        {row.daysRemaining != null ? (
                          <Badge
                            bg={
                              row.daysRemaining < 30
                                ? "danger"
                                : row.daysRemaining < 90
                                  ? "warning"
                                  : "secondary"
                            }
                          >
                            {row.daysRemaining}
                          </Badge>
                        ) : (
                          "\u2014"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </TabContainerWithTabs>
  );
};

export default FirmRMDReport;
